import baseAPI from '@/api/base';
import publicAPI from '@/api/public';
import {
  CopyWeeklyScheduleDay_N,
  GetWeeklySchedule_N,
  GetWeeklySchedulePublic_N,
  GetWeeklyScheduleSettings_N,
  SaveWeeklySchedule_N,
  SaveWeeklyScheduleDayNote_N,
  SaveWeeklyScheduleDisplays_N,
  SaveWeeklyScheduleItem_N,
  SaveWeeklyScheduleOverride_N,
  SaveWeeklyScheduleSettings_N,
  SaveWeeklyScheduleTemplate_N,
} from '@/dtos/weekly_schedule_N.dto';
import {
  WEEKLY_SCHEDULE_N_URL,
  WEEKLY_SCHEDULE_N_DAY_NOTE_URL,
  WEEKLY_SCHEDULE_N_DISPLAY_URL,
  WEEKLY_SCHEDULE_N_ITEM_COPY_DAY_URL,
  WEEKLY_SCHEDULE_N_ITEM_DUPLICATE_URL,
  WEEKLY_SCHEDULE_N_ITEM_URL,
  WEEKLY_SCHEDULE_N_ITEM_VERIFY_URL,
  WEEKLY_SCHEDULE_N_OVERRIDE_URL,
  WEEKLY_SCHEDULE_N_PUBLIC_URL,
  WEEKLY_SCHEDULE_N_SCHEDULE_DUPLICATE_URL,
  WEEKLY_SCHEDULE_N_SCHEDULE_TOGGLE_URL,
  WEEKLY_SCHEDULE_N_SCHEDULE_URL,
  WEEKLY_SCHEDULE_N_SETTINGS_URL,
  WEEKLY_SCHEDULE_N_TEMPLATE_ACTIVATE_URL,
  WEEKLY_SCHEDULE_N_TEMPLATE_DUPLICATE_URL,
  WEEKLY_SCHEDULE_N_TEMPLATE_URL,
} from '@/utils/urls';
import { AxiosResponse } from 'axios';

class WeeklySchedule_N {
  /* -------------------------------- schedules ------------------------------- */

  // The organization's weekly schedules, each summed up for its card.
  static getWeeklySchedules_N = async (params: { organizationId?: string }): Promise<AxiosResponse> =>
    baseAPI.get(WEEKLY_SCHEDULE_N_URL, { params });

  // One schedule's whole bundle: its seasons, items, notes, boards and settings.
  static getWeeklySchedule_N = async ({ id, ...params }: GetWeeklySchedule_N): Promise<AxiosResponse> =>
    baseAPI.get(`${WEEKLY_SCHEDULE_N_SCHEDULE_URL}/${encodeURIComponent(id)}`, { params });

  static registerSchedule_N = async (payload: SaveWeeklySchedule_N): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_SCHEDULE_URL, { ...payload });

  static editSchedule_N = async (payload: SaveWeeklySchedule_N): Promise<AxiosResponse> =>
    baseAPI.put(WEEKLY_SCHEDULE_N_SCHEDULE_URL, { ...payload });

  static toggleSchedule_N = async (payload: { id: string; isActive?: boolean; organizationId?: string }): Promise<AxiosResponse> =>
    baseAPI.patch(WEEKLY_SCHEDULE_N_SCHEDULE_TOGGLE_URL, { ...payload });

  static duplicateSchedule_N = async (payload: { id: string; name?: string; organizationId?: string }): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_SCHEDULE_DUPLICATE_URL, { ...payload });

  static deleteSchedule_N = async (id: string): Promise<AxiosResponse> =>
    baseAPI.delete(`${WEEKLY_SCHEDULE_N_SCHEDULE_URL}/${encodeURIComponent(id)}`);

  // Unauthenticated: what a public screen reads for itself. Through the
  // credential-free client, so a screen without a schedule (a 404) never
  // trips the admin client's error handling.
  static getWeeklySchedulePublic_N = async (params: GetWeeklySchedulePublic_N): Promise<AxiosResponse> =>
    publicAPI.get(WEEKLY_SCHEDULE_N_PUBLIC_URL, { params });

  /* -------------------------------- templates ------------------------------- */

  static registerTemplate_N = async (payload: SaveWeeklyScheduleTemplate_N): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_TEMPLATE_URL, { ...payload });

  static editTemplate_N = async (payload: SaveWeeklyScheduleTemplate_N): Promise<AxiosResponse> =>
    baseAPI.put(WEEKLY_SCHEDULE_N_TEMPLATE_URL, { ...payload });

  static activateTemplate_N = async (payload: { id: string; isActive?: boolean; organizationId?: string }): Promise<AxiosResponse> =>
    baseAPI.patch(WEEKLY_SCHEDULE_N_TEMPLATE_ACTIVATE_URL, { ...payload });

  static duplicateTemplate_N = async (payload: { id: string; name?: string; organizationId?: string }): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_TEMPLATE_DUPLICATE_URL, { ...payload });

  static deleteTemplate_N = async ({ id, scheduleId }: { id: string; scheduleId: string }): Promise<AxiosResponse> =>
    baseAPI.delete(`${WEEKLY_SCHEDULE_N_TEMPLATE_URL}/${id}`, { params: { scheduleId } });

  /* ---------------------------------- items --------------------------------- */

  static registerItem_N = async (payload: SaveWeeklyScheduleItem_N): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_ITEM_URL, { ...payload });

  static editItem_N = async (payload: SaveWeeklyScheduleItem_N): Promise<AxiosResponse> =>
    baseAPI.put(WEEKLY_SCHEDULE_N_ITEM_URL, { ...payload });

  static duplicateItem_N = async (payload: { id: string; title?: string; organizationId?: string }): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_ITEM_DUPLICATE_URL, { ...payload });

  static copyDay_N = async (payload: CopyWeeklyScheduleDay_N): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_ITEM_COPY_DAY_URL, { ...payload });

  static verifyItem_N = async (payload: { id: string; verified: boolean; organizationId?: string }): Promise<AxiosResponse> =>
    baseAPI.patch(WEEKLY_SCHEDULE_N_ITEM_VERIFY_URL, { ...payload });

  static deleteItem_N = async (id: string): Promise<AxiosResponse> =>
    baseAPI.delete(`${WEEKLY_SCHEDULE_N_ITEM_URL}/${id}`);

  /* -------------------------------- overrides ------------------------------- */

  static saveOverride_N = async (payload: SaveWeeklyScheduleOverride_N): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_OVERRIDE_URL, { ...payload });

  static deleteOverride_N = async ({ id, itemId }: { id: string; itemId: string }): Promise<AxiosResponse> =>
    baseAPI.delete(`${WEEKLY_SCHEDULE_N_OVERRIDE_URL}/${id}`, { params: { itemId } });

  /* -------------------------------- day notes ------------------------------- */

  static saveDayNote_N = async (payload: SaveWeeklyScheduleDayNote_N): Promise<AxiosResponse> =>
    baseAPI.post(WEEKLY_SCHEDULE_N_DAY_NOTE_URL, { ...payload });

  static deleteDayNote_N = async ({ id, scheduleId }: { id: string; scheduleId: string }): Promise<AxiosResponse> =>
    baseAPI.delete(`${WEEKLY_SCHEDULE_N_DAY_NOTE_URL}/${id}`, { params: { scheduleId } });

  /* --------------------------------- displays ------------------------------- */

  // Every screen and artboard the schedule is on, as one list.
  static saveDisplays_N = async (payload: SaveWeeklyScheduleDisplays_N): Promise<AxiosResponse> =>
    baseAPI.put(WEEKLY_SCHEDULE_N_DISPLAY_URL, { ...payload });

  /* --------------------------------- settings ------------------------------- */

  static getSettings_N = async (params: GetWeeklyScheduleSettings_N): Promise<AxiosResponse> =>
    baseAPI.get(WEEKLY_SCHEDULE_N_SETTINGS_URL, { params });

  static saveSettings_N = async (payload: SaveWeeklyScheduleSettings_N): Promise<AxiosResponse> =>
    baseAPI.put(WEEKLY_SCHEDULE_N_SETTINGS_URL, { ...payload });
}

export default WeeklySchedule_N;
